import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AutocompleteInput,
  BooleanInput,
  DateInput,
  ReferenceInput,
  regex,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  usePermissions,
  useTranslate,
} from 'react-admin';
import { Box, Typography } from '@material-ui/core';
import { nanoid } from 'nanoid';
import { InputsBlock, Toolbar } from '../../../components';
import { nationalities, parseDate } from '../../../helpers';
import { getUser } from '../../../user';
import FunctionSelectInput from './FunctionSelectInput';
import ContractEndTypeInput from './ContractEndTypeInput';
import ContractEndReasonInput from './ContractEndReasonInput';
import DocumentReferenceInput from './DocumentReferenceInput';
import ImportCleanerInput from './ImportCleanerInput';

import { contractDateValidation } from '../../../helpers/date';
import TimeClockBadgeInput from './TimeClockBadgeInput';

const validatePhoneNumber = regex(
  /^((\+33\s?[1-9](?:\s?\d{2}){4})|(0[1-9](?:\s?\d{2}){4}))$/,
  'Numéro de téléphone invalide',
);

const validateEmail = regex(
  /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
  'Email invalide',
);

const FormWrapper = ({ children }) => {
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%',
      }}
    >
      {children}
    </Box>
  );
};

const validate = (values) => {
  return Object.assign({}, contractDateValidation(values));
};

const Form = (props) => {
  const translate = useTranslate();
  const location = useLocation();
  // Fail closed: rely on react-admin permissions, fall back to the stored
  // user while the async AUTH_GET_PERMISSIONS call hasn't resolved yet
  // (same repli pattern as Vivier/components/WarningsCard.js).
  const { permissions } = usePermissions();
  const user = permissions || getUser();

  const { newSilaeId, duplicateFromId } = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return {
      newSilaeId: params.get('new_silae_id'),
      duplicateFromId: params.get('duplicate_from'),
    };
  }, [location.search]);

  const isCreate = !props.record || !props.record.id;

  // Mirror the server-side check (postgRest.js: hasPermission(user, 'cleaner',
  // 'delete', 1)) instead of a hardcoded role test, so the button disappears
  // by itself once a role loses the `cleaner.delete` permission. `!isCreate`
  // keeps the button off the Create screen (this Form is shared by
  // Cleaner/Create.js and Cleaner/Edit.js).
  const isDeletable =
    !isCreate &&
    !!(
      user &&
      user.permissions &&
      user.permissions.cleaner &&
      user.permissions.cleaner.delete >= 1
    );

  const initialValues = useMemo(() => {
    if (!isCreate) return undefined;
    const values = {};
    if (newSilaeId) values.silae_id = newSilaeId;
    if (duplicateFromId) values.import_cleaner_id = duplicateFromId;
    return Object.keys(values).length > 0 ? values : undefined;
  }, [isCreate, newSilaeId, duplicateFromId]);

  return (
    <SimpleForm
      {...props}
      component={FormWrapper}
      redirect="show"
      validate={validate}
      initialValues={initialValues}
      toolbar={<Toolbar isDeletable={isDeletable} />}
      save={(values, redirect) => {
        // eslint-disable-next-line camelcase
        const { import_cleaner_id, ...data } = values;
        return props.save(
          {
            ...data,
            id: values.id ?? nanoid(18),
            account_id: '0010Y00000Ijn8cQAB',
          },
          redirect,
        );
      }}
    >
      {isCreate && !newSilaeId && (
        <Box px={2} mt={2} mb={2}>
          <ImportCleanerInput />
        </Box>
      )}
      <InputsBlock title={translate('resources.cleaner.card_title.civil')}>
        <SelectInput
          source="salutation"
          choices={[
            { id: 'Mr.', name: 'Mr.' },
            { id: 'Mrs.', name: 'Mrs.' },
          ]}
          defaultValue="Mr."
          validate={[required()]}
        />
        <TextInput source="firstname" validate={[required()]} />
        <TextInput source="lastname" validate={[required()]} />
        <FunctionSelectInput />
        <ReferenceInput
          filterToQuery={
            /* istanbul ignore next */
            (searchText) => {
              return { fullname: searchText };
            }
          }
          sort={{ field: 'fullname', order: 'ASC' }}
          filter={{
            'in(function)': [
              'Manager',
              'Admin',
              'Team Leader',
              'Office Employee',
              'Customer Service',
            ],
            employee_status: 'Salarié actuel',
          }}
          perPage={50}
          source="manager_id"
          label="Manager"
          reference="cleaner"
          validate={[required()]}
        >
          <AutocompleteInput optionText="fullname" />
        </ReferenceInput>
        <TextInput source="email" validate={validateEmail} />
        <TextInput source="mobile_phone" validate={validatePhoneNumber} />
        <TextInput source="phone" validate={validatePhoneNumber} />
        <TextInput source="mailing_street" />
        <TextInput source="mailing_additional" />
        <TextInput source="mailing_postal_code" />
        <TextInput source="mailing_city" />
        <BooleanInput source="quartier_prioritaire_de_la_ville" />
        <DateInput source="birthdate" parse={parseDate} />
        <TextInput source="birthplace" />
        <SelectInput
          source="nationality"
          choices={nationalities}
          defaultValue="France"
        />
        <TextInput source="social_security_number" />
        <TextInput source="residence_permit_number" />
        <DateInput source="residence_permit_expiry_date" />
        <TextInput source="residence_permit_nature" />
        <TextInput source="payfit_id" />
        <TextInput source="silae_id" />
        <TextInput source="drive_folder_id" />
        {/* Plan 036 (frontière vivier) : "Vivier salarial" is not selectable at
            creation anymore — the vivier has its own creation form
            (Profil vivier / person_vivier) and must not be reachable from the
            generic Cleaner form, which used to let RH create duplicate vivier
            fiches here. Editing an existing fiche keeps the full choice list
            so a record already carrying this status still displays it. */}
        <SelectInput
          source="employee_status"
          validate={[required()]}
          choices={
            isCreate
              ? [
                  { id: 'Salarié actuel', name: 'Salarié actuel' },
                  { id: 'Ancien salarié', name: 'Ancien salarié' },
                ]
              : [
                  { id: 'Salarié actuel', name: 'Salarié actuel' },
                  { id: 'Ancien salarié', name: 'Ancien salarié' },
                  { id: 'Vivier salarial', name: 'Vivier salarial' },
                ]
          }
        />
      </InputsBlock>
      {/* Plan 036 : enfant direct de SimpleForm, pas de InputsBlock —
          InputsBlock fait Children.map(child => cloneElement(child, props))
          sans filtrer les enfants falsy ; en édition (isCreate=false) ce
          bloc conditionnel valait `false`, cloné quand même → crash "The
          argument must be a React element" (écran blanc, aucune error
          boundary). SimpleForm est sûr pour ce pattern : FormInput teste
          `input ? … : null` avant tout accès à `input.props` (cf.
          ImportCleanerInput plus haut), donc un enfant conditionnel ne
          casse rien ici. */}
      {isCreate && (
        <Typography variant="caption" color="textSecondary">
          Une fiche vivier se crée depuis{' '}
          <Link to="/person_vivier/create">Profil vivier</Link>.
        </Typography>
      )}

      <InputsBlock
        title={translate('resources.cleaner.card_title.health_care')}
      >
        <BooleanInput source="health_insurance_exemption" />
        <SelectInput
          sx={{ minWidth: 3000 }}
          source="health_insurance_exemption_reason"
          allowEmpty
          emptyText="ra.action.clear_input_value"
          choices={[
            { id: 'ACS', name: 'ACS' },
            { id: 'Apprenti', name: 'Apprenti' },
            {
              id: 'CDD de moins de 12 mois',
              name: 'CDD de moins de 12 mois',
            },
            { id: 'CMU', name: 'CMU' },
            { id: 'Contrat collectif', name: 'Contrat collectif' },
            { id: 'Contrat individuel', name: 'Contrat individuel' },
            { id: 'Contrat loi Madelin', name: 'Contrat loi Madelin' },
          ]}
        />
        <DateInput
          parse={parseDate}
          source="health_insurance_exemption_end_date"
        />
        <DocumentReferenceInput
          source="health_insurance_exemption_file"
          type="health_care"
        />
        <SelectInput
          source="occupational_medecine_reason"
          allowEmpty
          emptyText="ra.action.clear_input_value"
          choices={[
            {
              id: "Visite de reprise arrêt maladie d'au moins 30 jours",
              name: "Visite de reprise arrêt maladie d'au moins 30 jours",
            },
            {
              id: "Visite de reprise accident du travail d'au moins 30 jours",
              name: "Visite de reprise accident du travail d'au moins 30 jours",
            },
            {
              id: 'Visite de reprise maladie professionnelle',
              name: 'Visite de reprise maladie professionnelle',
            },
            {
              id: 'Visite de reprise congé maternité',
              name: 'Visite de reprise congé maternité',
            },
            {
              id: "Visite initiale d'information et de prévention",
              name: "Visite initiale d'information et de prévention",
            },
            { id: 'Visite de suivi', name: 'Visite de suivi' },
          ]}
        />
        <DateInput parse={parseDate} source="occupational_medecine_date" />
        <DocumentReferenceInput
          source="occupational_medecine_file"
          type="occupational_medecine"
        />
        <DateInput parse={parseDate} source="next_medical_visit_date" />
        <TextInput source="spvie_id" />
      </InputsBlock>

      <InputsBlock title={translate('resources.cleaner.card_title.contract')}>
        <BooleanInput source="is_taken_over" />
        <BooleanInput source="cdd_etudiant" />
        <DateInput
          source="contract_start_date"
          parse={parseDate}
          validate={[required()]}
        />
        <DateInput
          source="seniority_date"
          parse={parseDate}
          validate={[required()]}
        />
        <DateInput source="trial_period_renewal_signing" parse={parseDate} />
        <DateInput source="contract_end_date" parse={parseDate} />
        <ContractEndTypeInput source="contract_end_type" />
        <ContractEndReasonInput source="contract_end_reason" />
        <BooleanInput
          source="metadata.generateIntercontract"
          label="Générer les intercontrats"
          format={(v) => {
            // Opt-out : une valeur non renseignée (NULL/undefined) génère
            // les intercontrats, donc le toggle doit s'afficher ON par défaut.
            return v === undefined || v === null ? true : v;
          }}
        />
      </InputsBlock>
      <InputsBlock title="Badge">
        <TimeClockBadgeInput />
      </InputsBlock>
    </SimpleForm>
  );
};

export default Form;
